<template>
    <article class="grid grid-cols-1 gap-8 at1024:grid-cols-[25%_1fr] mb-16">
        <div v-if="article.categories" class="hidden at1024:block">
            <p class="sr-only">Article categories:</p>
            <ul class="space-y-2">
                <li v-for="category in article.categories">
                    <Link
                        :href="route('web.education.category.show', category.slug)"
                        class="inline-block px-6 py-2.5 bg-gray-125 text-gray-800 text-[13px] leading-tight uppercase font-medium tracking-widest hover:bg-gray-200"
                        v-text="category.name"
                    />
                </li>
            </ul>
        </div>

        <div class="space-y-14">
            <div class="space-y-4 at1024:space-y-6">
                <p v-if="article.published_at" class="mb-4">
                    <span class="sr-only">Published:</span>
                    <time class="type-tertiary text-sm">
                        {{ DateTime.fromISO(article.published_at).toFormat('DDD') }}
                    </time>
                </p>
                <h1 class="leading-none mb-[5px] text-3xl at1024:text-5xl at1024:leading-none font-bold block">
                    {{ article.title }}
                </h1>
                <p class="text-gray leading-normal text-base at1024:text-2xl dark:text-white">{{ article.headline }}</p>
            </div>

            <figure
                v-if="marketingAssetUrl(article.image)"
                class="mx-auto at1024:-ml-24 at1200:-ml-42"
            >
                <img
                    :src="marketingAssetUrl(article.image)"
                    :alt="article.image.data.alt_text || ''"
                    class="border"
                />
            </figure>

            <div class="prose-lg at1024:prose-xl">
                <div v-for="block in article.content" ref="articleContent">
                    <div v-html="block.content" class="custom-article-styles"></div>
                </div>
                <p class="text-base flex flex-wrap gap-2 items-baseline">
                    <strong class="uppercase">{{ $t('Posted in') }}:</strong>
                    <template v-for="category in article.categories">
                        <a :href="route('web.education.category.show',category.slug)" class="underline font-medium text-gray text-sm hover:text-black">{{ category.name }}</a>
                    </template>
                </p>
            </div>
            <ShareButtons
                class="print:hidden"
                :url="article.url"
                :title="article.title"
                :summary="article.headline"
            />
        </div>
    </article>

    <Meta
        :meta="article.meta"
        :title="article.title"
        :description="article.headline"
        :jsonLdMainEntity="jsonLdMainEntityBlogShow(article)"
        :jsonLdBreadcrumbs="crumbs"
        :ogImage="article.image?.external_id"
    />
</template>

<script setup>
import {inject, onMounted, ref, watch} from "vue";
import { Meta, ShareButtons } from "@/Components";
import {useJsonLd, useAssetTransforms, useImageDialog} from "@/Composables";
import {DateTime} from "luxon";

const { marketingAssetUrl } = useAssetTransforms();
const route = inject("route");
const props = defineProps(["article", "crumbs", "previousLink", "nextLink"]);
const { jsonLdMainEntityBlogShow } = useJsonLd();
</script>

<style scoped>
/* Your existing styles here */
.custom-article-styles img {
    position: relative;
}

.custom-article-styles img::after {
    /* Styles for the magnifying glass icon */
    content: '🔍';
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
    transition: opacity 0.3s;
}

.custom-article-styles img:hover::after {
    opacity: 1;
}
.custom-article-styles :deep(h4) {
    @apply font-medium text-2xl at1024:text-3xl mb-8;
}

.custom-article-styles :deep(p) {
    @apply leading-normal text-gray;
}

.dark .custom-article-styles :deep(p) {
    @apply text-white;
}

.custom-article-styles :deep(a) {
    @apply underline text-gray hover:text-red font-medium;
}

.dark .custom-article-styles :deep(a) {
    @apply text-white;
}

.custom-article-styles :deep(li) {
    @apply list-disc my-0;
}

.custom-article-styles :deep(li a) {
    @apply text-gray hover:text-gray font-normal;
}

.custom-article-styles :deep(iframe) {
    @apply w-full;
}

.custom-article-styles :deep(.vex-img img) {
    @apply border border-transparent hover:border-red p-4;
}

.custom-article-styles :deep(.vex-img) {
    @apply !cursor-zoom-in;
}
</style>
